// The menu area: "Menu metrics" controls, the heatmap image chart and the summary metrics
// table (menu_block.coffee composition). The chart area obeys the same components.chart +
// map/none gates as the other two (D10) -- the declared question and report modes, not the cm
// selection, exactly like block_base.renderChart.

import { useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { isMapOrNone } from '@aytm/app/stats/page/components/lib/utils'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'

import MenuMetricsControls from '@aytm/app/stats/page/question-views/mn/components/menu-section/MenuMetricsControls'
import MenuChart from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/MenuChart'
import { calcMenuMetricsTable } from '@aytm/app/stats/page/question-views/mn/components/menu-section/lib/menuMetricsTable'
import { MENU_VIEW_OPTIONS } from '@aytm/app/stats/page/question-views/mn/lib/viewOptions'
import { calcMenuChartHeight } from '@aytm/app/stats/page/question-views/mn/components/menu-section/lib/constants'

const MenuSection = ({
  id,
  components,
  answer,
  format,
  image,
  sections,
  priceTemplate,
  na,
  isAnimated,
  menuViewMode,
  menuCollapsed,
  chartMode,
  globalChartMode,
  onModelChange,
  onToggle,
  onExport
}) => {
  // D13(a): while a delivery re-fetch is in flight the previously rendered summary stays on
  // screen — the coffee items list early-returns on 'loading' when it has rendered before
  const tableProps = useMemo(
    () => calcMenuMetricsTable({ id, answer, format, priceTemplate, na }),
    [id, answer, format, priceTemplate, na]
  )

  const lastTableProps = useRef(null)
  const shownTableProps = 'loading' === answer && lastTableProps.current ? lastTableProps.current : tableProps

  if ('loading' !== answer) lastTableProps.current = tableProps

  return (
    <>
      {isComponentOn(components, 'controls') && (
        <MenuMetricsControls
          title="Menu metrics"
          options={MENU_VIEW_OPTIONS}
          viewMode={menuViewMode}
          collapsed={menuCollapsed}
          onModelChange={onModelChange}
          onToggle={onToggle}
        />
      )}
      {!menuCollapsed && (
        <>
          {isComponentOn(components, 'chart') && !isMapOrNone(chartMode, globalChartMode) && (
            <ChartPlaceholder
              height={calcMenuChartHeight(image)}
              required
              isLoading={'loading' === answer}
              isAnimated={isAnimated}
            >
              {'loading' !== answer && (
                <MenuChart
                  id={id}
                  image={image}
                  answer={answer}
                  sections={sections}
                  menuViewMode={menuViewMode}
                  isAnimated={isAnimated}
                  onExport={onExport}
                />
              )}
            </ChartPlaceholder>
          )}
          {isComponentOn(components, 'table') && <Table {...shownTableProps} />}
        </>
      )}
    </>
  )
}

MenuSection.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  components: PropTypes.object,
  answer: answerPropType,
  format: PropTypes.object,
  image: PropTypes.object,
  sections: PropTypes.array,
  priceTemplate: PropTypes.string,
  na: PropTypes.bool,
  isAnimated: PropTypes.bool,
  menuViewMode: PropTypes.string,
  menuCollapsed: PropTypes.bool,
  chartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  onModelChange: PropTypes.func,
  onToggle: PropTypes.func,
  onExport: PropTypes.func
}

MenuSection.displayName = 'MenuSection'

export default MenuSection
